/**
 * ESM twin of helpers/webixNativeScroll.js for Vite (app.js) and vitest.
 *
 * Mobile init is the documented Webix snippet:
 *   if (webix.env.touch) { webix.ui.fullScreen(); webix.Touch.limit(true); }
 *   else if (webix.env.scrollSize) { webix.CustomScroll.init(); }
 */

const DEFAULT_FILTER_MIN_WIDTH = 1100;

const isTouchScrollDevice = function (env) {
    return !!(env && env.touch);
};

const limitTouch = function (webixApi) {
    if (!webixApi || !webixApi.env || !webixApi.env.touch || !webixApi.Touch) {
        return false;
    }
    if (typeof webixApi.Touch.enable === "function") {
        webixApi.Touch.enable();
    }
    if (typeof webixApi.Touch.limit === "function") {
        webixApi.Touch.limit(true);
    }
    return true;
};

/**
 * Webix fullScreen() writes body.style.width/height in px (the mobile inner
 * size). CSS width:100% on the app root is then 100% of that locked body, so
 * leaving DevTools device mode / zooming leaves a left-hand strip.
 * Always pin body to the current window, then ui.resize().
 */
const syncBodyToWindow = function (width, height, bodyStyle) {
    if (!bodyStyle || !height) {
        return { width: null, height: null };
    }
    bodyStyle.width = width + "px";
    bodyStyle.height = height + "px";
    return { width: bodyStyle.width, height: bodyStyle.height };
};

const relayoutOverlays = function (webixApi, size) {
    if (!webixApi || typeof webixApi.$$ !== "function") {
        return false;
    }
    size = size || {};
    var menu = webixApi.$$("menu");
    if (menu && menu.config) {
        var toolbar = webixApi.$$("toolbar2");
        var top = (toolbar && toolbar.$height) || 38;
        var height = Math.max(0, (size.innerHeight || 0) - top);
        if (!height && typeof window !== "undefined") {
            height = Math.max(0, window.innerHeight - top);
        }
        var width = menu.config.width || 340;
        if (typeof menu.define === "function") {
            if (height) {
                menu.define({ height: height, width: width });
            }
            menu.define("state", function (state) {
                if (!state) {
                    return;
                }
                state.top = top;
                state.height = Math.max(0, ((typeof window !== "undefined" ? window.innerHeight : size.innerHeight) || 0) - top);
            });
        }
        if (!menu.config.hidden) {
            if (typeof menu.setPosition === "function") {
                menu.setPosition(0, top);
            }
            if (typeof menu.resize === "function") {
                menu.resize();
            }
        }
    }
    var tree = webixApi.$$("app:menu");
    if (tree && typeof tree.resize === "function") {
        tree.resize();
    }
    if (webixApi.CustomScroll && typeof webixApi.CustomScroll.resize === "function"
        && !(webixApi.env && webixApi.env.touch)) {
        webixApi.CustomScroll.resize();
    }
    return !!(menu || tree);
};

const relayoutViewport = function (webixApi, win, doc) {
    win = win || (typeof window !== "undefined" ? window : null);
    doc = doc || (typeof document !== "undefined" ? document : null);
    if (!win || !doc || !doc.body) {
        return false;
    }
    syncBodyToWindow(win.innerWidth, win.innerHeight, doc.body.style);
    if (webixApi && webixApi.ui && typeof webixApi.ui.resize === "function") {
        webixApi.ui.resize();
    }
    relayoutOverlays(webixApi, { innerWidth: win.innerWidth, innerHeight: win.innerHeight });
    return true;
};

/** Webix fullScreen onrotate sets _freeze_resize for 500ms — retry after that. */
const FREEZE_RETRY_MS = 560;

var viewportWatchStarted = false;

/**
 * URL-bar / visualViewport height-only changes must not call ui.resize:
 * that resets scrollview position (jump to top of the report).
 */
const viewportNeedsRelayout = function (last, next, bodyWidthPx) {
    last = last || {};
    next = next || {};
    var width = next.w;
    var expected = width ? width + "px" : "";
    if (bodyWidthPx && expected && bodyWidthPx !== expected) {
        return true;
    }
    if (!last.w || last.w !== width) {
        return true;
    }
    return false;
};

const watchViewport = function (webixApi, win, doc) {
    win = win || (typeof window !== "undefined" ? window : null);
    doc = doc || (typeof document !== "undefined" ? document : null);
    if (!win || !doc || !doc.body || typeof win.addEventListener !== "function") {
        return function () {};
    }
    if (viewportWatchStarted && arguments.length < 2) {
        return function () {};
    }
    viewportWatchStarted = arguments.length < 2;

    var last = { w: 0, h: 0 };
    var timer = null;
    var lateTimer = null;

    var apply = function () {
        var w = win.innerWidth;
        var h = win.innerHeight;
        if (!viewportNeedsRelayout(last, { w: w, h: h }, doc.body.style.width)) {
            last.h = h;
            return false;
        }
        last = { w: w, h: h };
        return relayoutViewport(webixApi, win, doc);
    };

    var schedule = function () {
        var willRelayout = viewportNeedsRelayout(last, {
            w: win.innerWidth,
            h: win.innerHeight
        }, doc.body.style.width);
        if (!willRelayout) {
            last.h = win.innerHeight;
            return;
        }
        if (timer) {
            clearTimeout(timer);
        }
        timer = setTimeout(function () {
            apply();
        }, 50);
        if (lateTimer) {
            clearTimeout(lateTimer);
        }
        lateTimer = setTimeout(function () {
            last.w = 0;
            apply();
        }, FREEZE_RETRY_MS);
    };

    apply();
    win.addEventListener("resize", schedule);
    if (win.visualViewport && typeof win.visualViewport.addEventListener === "function") {
        win.visualViewport.addEventListener("resize", schedule);
    }
    return function unwatch() {
        win.removeEventListener("resize", schedule);
        if (win.visualViewport && typeof win.visualViewport.removeEventListener === "function") {
            win.visualViewport.removeEventListener("resize", schedule);
        }
        if (timer) {
            clearTimeout(timer);
        }
        if (lateTimer) {
            clearTimeout(lateTimer);
        }
        viewportWatchStarted = false;
    };
};

const applyMobileEnv = function (webixApi) {
    if (!webixApi || !webixApi.env) {
        return false;
    }
    var touch = false;
    if (webixApi.env.touch) {
        if (webixApi.ui && typeof webixApi.ui.fullScreen === "function") {
            webixApi.ui.fullScreen();
        }
        limitTouch(webixApi);
        touch = true;
    } else if (webixApi.env.scrollSize && webixApi.CustomScroll && typeof webixApi.CustomScroll.init === "function") {
        webixApi.CustomScroll.init();
    }
    watchViewport(webixApi);
    return touch;
};

const wrapFilterScroll = function (filterConfig, options) {
    options = options || {};
    if (!filterConfig) {
        return filterConfig;
    }
    if (String(filterConfig.css || "").indexOf("report-filters-mobile") !== -1) {
        return filterConfig;
    }

    var body = Object.assign({}, filterConfig);
    if (body.minWidth == null) {
        body.minWidth = options.minWidth != null ? options.minWidth : DEFAULT_FILTER_MIN_WIDTH;
    }
    if (body.autoheight == null) {
        body.autoheight = true;
    }

    var wrap = {
        view: "scrollview",
        scroll: "x",
        css: "report-filter-scroll",
        body: body
    };
    if (options.id) {
        wrap.id = options.id;
    }
    return wrap;
};

export default {
    DEFAULT_FILTER_MIN_WIDTH: DEFAULT_FILTER_MIN_WIDTH,
    FREEZE_RETRY_MS: FREEZE_RETRY_MS,
    isTouchScrollDevice: isTouchScrollDevice,
    applyMobileEnv: applyMobileEnv,
    applyNativeScroll: applyMobileEnv,
    limitTouch: limitTouch,
    syncBodyToWindow: syncBodyToWindow,
    relayoutViewport: relayoutViewport,
    relayoutOverlays: relayoutOverlays,
    watchViewport: watchViewport,
    viewportNeedsRelayout: viewportNeedsRelayout,
    wrapFilterScroll: wrapFilterScroll
};
